import jsPDF from "jspdf";
import moment from "moment";
import { ToWords } from "to-words";

const formatDate = (date) => {
  if (!date) return "-";
  const parsed = moment(date);
  return parsed.isValid() ? parsed.format("DD/MM/YYYY") : "-";
};

const formatAmount = (value) => {
  const amount = Math.round(Number(value) || 0);
  return new Intl.NumberFormat("en-IN", {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(amount);
};

const sanitizeFileName = (value) =>
  String(value || "Purchase_Order").replace(/[\\/:*?"<>|]/g, "_");

const toWords = new ToWords({
  localeCode: "en-IN",
  converterOptions: {
    currency: false,
    ordinal: false,
  },
});

/** Defaults match billing invoice PDF filters (no logo, no annexure for PO). */
export const DEFAULT_PO_PDF_FILTERS = {
  showPeriod: true,
  showZeroAmountInventories: true,
  showItemAmount: false,
  showSeparateProductionCharges: false,
  showHoardingWisePrintingMounting: false,
  showPrintingMountingOnly: false,
  isFromSubBdf: false,
};

const getMaintenance = (item) => Number(item?.maintenanceRate) || 0;
const getPrinting = (item) => Number(item?.printingCharge) || 0;
const getMounting = (item) => Number(item?.mountingCharge) || 0;

const CHARGE_TYPE_LABELS = {
  AGENCY_COMMISSION: "Agency Commission",
  TRANSPORTATION: "Transportation",
};

const invLineMeta = (item) => ({
  location: item?.lineType
    ? CHARGE_TYPE_LABELS[item.lineType] || item.lineType
    : item?.invId?.locations?.name || "NA",
  vehicle: item?.lineType ? "-" : item?.invId?.typeOfMedia?.name || "NA",
  size:
    item?.invId?.width && item?.invId?.height
      ? `${item.invId.width} x ${item.invId.height}`
      : "NA",
  from: formatDate(item?.startDate),
  to: formatDate(item?.endDate),
});

/**
 * Builds the PO jsPDF document (use for preview or pass to `.save()`).
 * @param {Array} tradingInventories
 * @param {object} bdfDetails
 * @param {string} poNumber
 * @param {Partial<typeof DEFAULT_PO_PDF_FILTERS>} filters
 * @returns {Promise<import("jspdf").jsPDF>}
 */
export const buildPurchaseOrderPdfDoc = async (
  tradingInventories = [],
  bdfDetails,
  poNumber,
  filters = {}
) => {
  const f = { ...DEFAULT_PO_PDF_FILTERS, ...filters };

  const doc = new jsPDF("p", "mm", "a4");
  const pageHeight = doc.internal.pageSize.getHeight();
  const isChargeOnly =
    tradingInventories.length > 0 &&
    tradingInventories.every((item) => item?.lineType);
  const columnWidths = isChargeOnly
    ? [10, 108, 0, 0, 22, 22, 28]
    : [10, 65, 27, 16, 22, 22, 28];
  const visibleColumnIndexes = isChargeOnly
    ? new Set([0, 1, 4, 5, 6])
    : new Set([0, 1, 2, 3, 4, 5, 6]);
  const tableLeftX = 10;
  const tableRightX = 200;
  const amountColumnLeftX = tableRightX - columnWidths[columnWidths.length - 1];
  const fixedBodyBottomY = 186;
  const rowHeight = 8;
  const maxRowsPerPage = 9;

  const apiBaseUrl = process.env.REACT_APP_API_BASE_URL;
  const token = window.localStorage.getItem("token");
  const firstOwnedBy =
    tradingInventories[0]?.invId?.ownedBy ||
    tradingInventories[0]?.commissionForId?.name ||
    "Vendor";

  const fetchVendors = async () => {
    try {
      const response = await fetch(
        `${apiBaseUrl}/inventoryManagement/getAllTradingParties`,
        {
          method: "GET",
          credentials: "include",
          headers: token ? { authorization: `Token ${token}` } : undefined,
        }
      );
      const responseJson = await response.json();

      if (responseJson?.success) {
        return responseJson.data?.map((party) => ({
          name: party?.name,
          gstNumber: party?.gstNumber,
          address: party?.address,
        }));
      }
    } catch (error) {
      console.error("Error fetching vendors:", error);
    }

    return [];
  };

  const directGstNumber = tradingInventories[0]?.commissionForId?.gstNumber;
  const directVendorAddress =
    tradingInventories[0]?.commissionForId?.address;
  const vendors = await fetchVendors();
  const matchedVendor = vendors.find((vendor) => vendor?.name === firstOwnedBy);
  const vendorAddress = directVendorAddress || matchedVendor?.address || "-";
  const gstNumber = directGstNumber || matchedVendor?.gstNumber || "-";
  const currentDate = moment().format("DD/MM/YYYY");

  const list = Array.isArray(tradingInventories) ? tradingInventories : [];
  const invLevelP = Number(bdfDetails?.printingCharge) || 0;
  const invLevelM = Number(bdfDetails?.mountingCharge) || 0;
  const sumLineP = list.reduce((s, i) => s + getPrinting(i), 0);
  const sumLineM = list.reduce((s, i) => s + getMounting(i), 0);
  const useInvoiceLevelSplit =
    (invLevelP > 0 || invLevelM > 0) && sumLineP === 0 && sumLineM === 0;

  const totalMaintAll = list.reduce((s, i) => s + getMaintenance(i), 0);

  const printingMountingForLine = (item) => {
    if (useInvoiceLevelSplit) {
      const w =
        totalMaintAll > 0
          ? getMaintenance(item) / totalMaintAll
          : list.length > 0
            ? 1 / list.length
            : 1;
      return { p: invLevelP * w, m: invLevelM * w };
    }
    return { p: getPrinting(item), m: getMounting(item) };
  };

  const totalProductionComputed = useInvoiceLevelSplit
    ? invLevelP + invLevelM
    : sumLineP + sumLineM;

  /** Legacy PO PDF was maintenance-only unless a printing/mounting filter is used (or annexure). */
  const includeProductionRows =
    f.showSeparateProductionCharges ||
    f.showHoardingWisePrintingMounting ||
    f.showPrintingMountingOnly;

  const displayTotal =
    (f.showPrintingMountingOnly ? 0 : totalMaintAll) +
    (includeProductionRows ? totalProductionComputed : 0);

  const totalFormatted = formatAmount(displayTotal);
  const amountInWords = `${toWords.convert(Math.round(displayTotal))} Only`;
  const formattedAmountInWords = amountInWords
    .toLowerCase()
    .split(" ")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
  const campaignName = bdfDetails?.brandId?.brandName || "NA";

  const bdfDateStr = formatDate(bdfDetails?.bdfDate);
  const productionFromTo =
    f.showPeriod && f.isFromSubBdf ? bdfDateStr : "";

  const aggPrintForFooterRows = useInvoiceLevelSplit ? invLevelP : sumLineP;
  const aggMountForFooterRows = useInvoiceLevelSplit ? invLevelM : sumLineM;

  const passesZeroFilterMaintenance = (item) =>
    f.showZeroAmountInventories || getMaintenance(item) !== 0;

  const passesZeroFilterHoarding = (item) => {
    if (f.showZeroAmountInventories) return true;
    const { p, m } = printingMountingForLine(item);
    return getMaintenance(item) !== 0 || p !== 0 || m !== 0;
  };

  const rows = [];

  if (f.showHoardingWisePrintingMounting) {
    const billedList = list.filter(passesZeroFilterHoarding);
    let srNo = 0;
    billedList.forEach((item) => {
      const { p, m } = printingMountingForLine(item);
      const meta = invLineMeta(item);
      srNo += 1;
      rows.push([
        `${srNo}`,
        `${meta.location} - Printing Charge`,
        meta.vehicle,
        meta.size,
        meta.from,
        meta.to,
        f.showItemAmount && srNo === 1
          ? totalFormatted
          : f.showItemAmount
            ? ""
            : formatAmount(p),
      ]);
      srNo += 1;
      rows.push([
        `${srNo}`,
        `${meta.location} - Mounting Charge`,
        meta.vehicle,
        meta.size,
        meta.from,
        meta.to,
        f.showItemAmount ? "" : formatAmount(m),
      ]);
    });
  } else {
    if (!f.showPrintingMountingOnly) {
      const forRows = list.filter(passesZeroFilterMaintenance);
      forRows.forEach((item, index) => {
        const meta = invLineMeta(item);
        rows.push([
          `${index + 1}`,
          meta.location,
          meta.vehicle,
          meta.size,
          meta.from,
          meta.to,
          f.showItemAmount && index === 0
            ? totalFormatted
            : f.showItemAmount
              ? ""
              : formatAmount(getMaintenance(item)),
        ]);
      });
    }
  }

  const billedInvsLength = f.showHoardingWisePrintingMounting
    ? list.filter(passesZeroFilterHoarding).length
    : f.showPrintingMountingOnly
      ? 0
      : list.filter(passesZeroFilterMaintenance).length;
  const effectiveBilledInvsRowCount = f.showHoardingWisePrintingMounting
    ? billedInvsLength * 2
    : billedInvsLength;

  const nextSerialNumber = f.showPrintingMountingOnly
    ? 1
    : effectiveBilledInvsRowCount + 1;

  if (includeProductionRows && !f.showHoardingWisePrintingMounting) {
    if (!f.showSeparateProductionCharges) {
      const combined = aggPrintForFooterRows + aggMountForFooterRows;
      if (combined > 0) {
        rows.push([
          `${nextSerialNumber}`,
          "Printing & Mounting Charges",
          "",
          "",
          productionFromTo,
          productionFromTo,
          f.showItemAmount ? "" : formatAmount(combined),
        ]);
      }
    } else {
      if (aggPrintForFooterRows > 0) {
        rows.push([
          `${nextSerialNumber}`,
          "Printing Charges",
          "",
          "",
          productionFromTo,
          productionFromTo,
          f.showItemAmount ? "" : formatAmount(aggPrintForFooterRows),
        ]);
      }
      if (aggMountForFooterRows > 0) {
        rows.push([
          `${nextSerialNumber + (aggPrintForFooterRows > 0 ? 1 : 0)}`,
          "Mounting Charges",
          "",
          "",
          productionFromTo,
          productionFromTo,
          f.showItemAmount ? "" : formatAmount(aggMountForFooterRows),
        ]);
      }
    }
  }

  const hasContent = rows.some((r) =>
    String(r[1] ?? "")
      .trim()
      .replace(/-/g, "")
  );
  if (!hasContent) {
    rows.push(["", "No line items match the selected filters", "", "", "", "", ""]);
  }

  if (rows.length <= 4) {
    rows.push(...Array(2).fill(["", "", "", "", "", "", ""]));
  }

  let startY = 70;
  let newSectionY;
  let newSectionHeight;
  let rowCount = 0;
  let isFirstPage = true;

  const addHeader = () => {
    doc.setFont("helvetica", "bold").setFontSize(18);
    doc.text("Yellow Spot OOH", 105, 18, { align: "center" });

    doc.setFont("helvetica", "normal").setFontSize(9);
    const headerAddress =
      "12th Block, 4th Floor, Magnet Corporate Park, Opp. Grand Cambay, Thaltej, Ahmedabad-380054.";
    doc.text(headerAddress, 105, 25, {
      align: "center",
      maxWidth: 170,
    });
    doc.text("Phone No.: 9033001770", 105, 31, { align: "center" });

    doc.setFont("helvetica", "bold").setFontSize(14);
    doc.text("PURCHASE ORDER", 105, 44, { align: "center" });

    const addressLeftY = 50;
    const vendorText = [
      "To,",
      firstOwnedBy || "N/A",
      vendorAddress || "N/A",
      `GST No. : ${gstNumber}`,
    ];
    const lineHeight = 5;
    const wrappedLeftText = [];
    const partyName = firstOwnedBy || "N/A";
    const wrappedPartyName = doc.splitTextToSize(partyName, 88);

    for (let i = 0; i < vendorText.length; i++) {
      if (i === 1) {
        wrappedLeftText.push(...wrappedPartyName);
      } else {
        const wrapped = doc.splitTextToSize(vendorText[i], 88);
        wrappedLeftText.push(...wrapped);
      }
    }

    const addressLeftHeight = wrappedLeftText.length * lineHeight + 2;
    const addressRightText = [
      `PO No.: ${poNumber || "NA"}`,
      `Date: ${currentDate}`,
    ];
    const rightSectionLineGap = 5;
    const campaignStartY = addressLeftY + 5 + addressRightText.length * rightSectionLineGap;
    const campaignLabel = "Campaign:";
    const campaignLabelX = 110;
    const campaignInlineText = `${campaignLabel} ${campaignName}`;
    const maxCampaignWidth = 100;
    const wrappedCampaignInline = doc.splitTextToSize(
      campaignInlineText,
      maxCampaignWidth
    );
    const addressRightHeight = Math.max(
      24,
      12 +
        addressRightText.length * rightSectionLineGap +
        wrappedCampaignInline.length * lineHeight
    );
    const maxHeight = Math.max(addressLeftHeight, addressRightHeight);

    doc.rect(10, addressLeftY, 95, maxHeight);
    doc.rect(105, addressLeftY, 95, maxHeight);

    wrappedLeftText.forEach((line, index) => {
      const isBold =
        wrappedPartyName.includes(line) ||
        index === wrappedLeftText.length - 1;
      doc.setFont("helvetica", isBold ? "bold" : "normal").setFontSize(10);
      doc.text(line, 15, addressLeftY + 5 + index * lineHeight);
    });

    doc.setFont("helvetica", "bold").setFontSize(10);
    addressRightText.forEach((line, index) => {
      doc.text(line, 110, addressLeftY + 5 + index * rightSectionLineGap);
    });

    wrappedCampaignInline.forEach((line, index) => {
      doc.text(line, campaignLabelX, campaignStartY + index * lineHeight);
    });

    startY = addressLeftY + maxHeight;

    doc.rect(10, startY, 190, rowHeight);
    doc.line(10, startY, 10, startY + rowHeight);
    doc.line(20, startY, 20, startY + rowHeight);
    doc.line(amountColumnLeftX, startY, amountColumnLeftX, startY + rowHeight);
    doc.line(tableRightX, startY, tableRightX, startY + rowHeight);

    const headers = [
      "Sr",
      "Media/Location",
      "Media Vehicle",
      "Size",
      f.showPeriod ? "From Date" : "",
      f.showPeriod ? "To Date" : "",
      "Amount",
    ];

    doc.setFont("helvetica", "bold").setFontSize(10);
    headers.forEach((header, i) => {
      if (!visibleColumnIndexes.has(i)) return;
      const columnStart = 10 + columnWidths.slice(0, i).reduce((a, b) => a + b, 0);
      const columnCenter = columnStart + columnWidths[i] / 2;
      doc.text(header, columnCenter, startY + 5, {
        align: "center",
        maxWidth: columnWidths[i] - 2,
      });
    });

    doc.setFont("helvetica", "normal").setFontSize(10);
    startY += rowHeight;
  };

  const drawSummaryAndFooter = (summaryStartY) => {
    const firstSectionY = summaryStartY;
    const firstSectionHeight = 28;
    doc.rect(10, firstSectionY, 95, firstSectionHeight);
    doc.setFont("helvetica", "bold").setFontSize(10);
    doc.text(
      `GST No.: 24AADFY0077E1ZE`,
      15,
      firstSectionY + 15
    );

    const secondSectionX = 105;
    const thirdSectionX = amountColumnLeftX;
    const secondSectionWidth = thirdSectionX - secondSectionX;
    const thirdSectionWidth = tableRightX - thirdSectionX;
    doc.rect(secondSectionX, firstSectionY, secondSectionWidth, firstSectionHeight);
    doc.rect(thirdSectionX, firstSectionY, thirdSectionWidth, firstSectionHeight);

    doc.setFont("helvetica", "normal").setFontSize(10);
    doc.text("Total", secondSectionX + 5, firstSectionY + 8);
    doc.text(totalFormatted, thirdSectionX + 2, firstSectionY + 8);
    doc.line(
      secondSectionX,
      firstSectionY + 18,
      secondSectionX + secondSectionWidth,
      firstSectionY + 18
    );
    doc.line(
      thirdSectionX,
      firstSectionY + 18,
      thirdSectionX + thirdSectionWidth,
      firstSectionY + 18
    );
    doc.setFont("helvetica", "bold").setFontSize(10);
    doc.text("Grand Total", secondSectionX + 5, firstSectionY + 25);
    doc.text(totalFormatted, thirdSectionX + 2, firstSectionY + 25);

    newSectionY = firstSectionY + firstSectionHeight;
    newSectionHeight = 10;
    doc.rect(10, newSectionY, 190, newSectionHeight);
    doc.setFont("helvetica", "bold").setFontSize(10);
    doc.text("Rupees :-", 15, newSectionY + 7);
    doc.setFont("helvetica", "normal").setFontSize(10);
    doc.text(formattedAmountInWords, 35, newSectionY + 7);
  };

  const addFooter = () => {
    const finalSectionY = newSectionY + newSectionHeight;
    doc.setFont("helvetica", "bold").setFontSize(10);
    doc.text("Terms & Conditions:", 15, finalSectionY + 5);

    const termsText = [
      "1. Bill should be in favour of Yellow Spot OOH.",
      "2. Kindly attach all supportive documents with the bill.",
      "3. Subject to Ahmedabad Jurisdiction.",
    ];

    doc.setFont("helvetica", "normal").setFontSize(8);
    const lineSpacing = 2.8;
    const pointSpacing = 1;
    let currentY = finalSectionY + 10;

    termsText.forEach((text) => {
      const wrappedText = doc.splitTextToSize(text, 100);
      wrappedText.forEach((line) => {
        doc.text(line, 15, currentY);
        currentY += lineSpacing;
      });
      currentY += pointSpacing;
    });

    const leftSectionHeight = currentY - finalSectionY + 20;
    doc.rect(10, finalSectionY, 115, leftSectionHeight);
    doc.rect(125, finalSectionY, 75, leftSectionHeight);
    doc.setFont("helvetica", "bold").setFontSize(10);
    doc.text("For, Yellow Spot OOH", 150, finalSectionY + 8);
    doc.text("[Authorised Signatory]", 150, finalSectionY + leftSectionHeight - 5);
    doc.addImage(
      "/images/yellowspotStamp.png",
      "PNG",
      158,
      finalSectionY + 10,
      22,
      22
    );
  };

  addHeader();

  rows.forEach((row, rowIndex) => {
    const isLastRow = rowIndex === rows.length - 1;
    const pageLimit = isLastRow ? 186 : pageHeight - 12;

    if (rowCount >= maxRowsPerPage || startY + rowHeight > pageLimit) {
      if (isFirstPage) {
        isFirstPage = false;
      }
      doc.addPage();
      addHeader();
      rowCount = 0;
    }

    let currentX = 10;
    let rowTotalHeight = rowHeight;

    row.forEach((text, index) => {
      if (!visibleColumnIndexes.has(index)) return;
      if (!f.showPeriod && (index === 4 || index === 5)) {
        currentX += columnWidths[index];
        return;
      }

      const isLocationColumn = index === 1;
      const availableWidth = isLocationColumn
        ? columnWidths[index] - 6
        : columnWidths[index] - 3;
      const wrappedText = doc.splitTextToSize(String(text ?? ""), availableWidth);

      rowTotalHeight = Math.max(rowTotalHeight, wrappedText.length * 5);

      const textY =
        startY + rowTotalHeight / 2 - (wrappedText.length * 5) / 2 + 3;

      wrappedText.forEach((line, lineIndex) => {
        const xPosition =
          isLocationColumn ? currentX + 3 : currentX + columnWidths[index] / 2;

        doc.setFont("helvetica", "normal").setFontSize(10);
        doc.text(line, xPosition, textY + lineIndex * 5 + 2, {
          align: isLocationColumn ? "left" : "center",
          maxWidth: isLocationColumn ? columnWidths[index] - 6 : columnWidths[index] - 2,
        });
      });

      currentX += columnWidths[index];
    });

    doc.line(10 + columnWidths[0], startY, 10 + columnWidths[0], startY + rowTotalHeight + 2);
    doc.line(amountColumnLeftX, startY, amountColumnLeftX, startY + rowTotalHeight + 2);
    doc.line(tableRightX, startY, tableRightX, startY + rowTotalHeight + 2);
    doc.line(10, startY, 10, startY + rowTotalHeight + 2);

    if (rowIndex === 0) {
      doc.line(10, startY, 200, startY);
    }

    startY += rowTotalHeight + 2;
    rowCount++;
  });

  if (startY < fixedBodyBottomY) {
    doc.line(tableLeftX, startY, tableLeftX, fixedBodyBottomY);
    doc.line(
      tableLeftX + columnWidths[0],
      startY,
      tableLeftX + columnWidths[0],
      fixedBodyBottomY
    );
    doc.line(amountColumnLeftX, startY, amountColumnLeftX, fixedBodyBottomY);
    doc.line(tableRightX, startY, tableRightX, fixedBodyBottomY);
    startY = fixedBodyBottomY;
  }

  doc.line(10, startY, 200, startY);
  drawSummaryAndFooter(startY);
  addFooter();

  if (doc.getNumberOfPages() > 1) {
    const totalPages = doc.getNumberOfPages();
    for (let i = 1; i < totalPages; i++) {
      doc.setPage(i);
      const currentPageBottomY = 285;
      doc.line(10, currentPageBottomY, 200, currentPageBottomY);
    }
    doc.setPage(totalPages);
  }

  return doc;
};

/**
 * Generates and downloads the PO PDF file.
 * @param {Array} tradingInventories
 * @param {object} bdfDetails
 * @param {string} poNumber
 * @param {Partial<typeof DEFAULT_PO_PDF_FILTERS>} filters
 */
const generatePurchaseOrderPDF = async (
  tradingInventories = [],
  bdfDetails,
  poNumber,
  filters = {}
) => {
  const doc = await buildPurchaseOrderPdfDoc(
    tradingInventories,
    bdfDetails,
    poNumber,
    filters
  );
  const firstOwnedBy =
    tradingInventories[0]?.invId?.ownedBy ||
    tradingInventories[0]?.commissionForId?.name ||
    "Vendor";
  doc.save(`${sanitizeFileName(firstOwnedBy)}_PO.pdf`);
};

export default generatePurchaseOrderPDF;
